import React from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import InactivityManager from 'utils/InactivityManager';
import styles from './KnowMyNumbers.module.scss';
import { t } from '../translations';
import setInitialSize from 'utils/zoomUtil';

const TIMEOUT = process.env.REACT_APP_INACTIVITY_TIMEOUT;

const REASON = {
  didNotTouchImage: 'Did not touch image to color'
};

class KnowMyNumbers extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      revealNumber: false,
      revealImage: false,
      revealColor: false,
      showAriaLabel: false
    };

    this.finishedPlaying = false;
    this.inactivityManager = new InactivityManager(
      TIMEOUT,
      () => this.playAudio('beforeColor'),
      REASON.didNotTouchImage
    );
    this.timers = [];
  }

  componentDidMount() {
    const tID = setTimeout(() => {
      this.playAudio('sayWord');
      this.setState({ revealNumber: true });
      this.inactivityManager.watch();
    }, 500);
    this.timers.push(tID);
  }

  componentWillUnmount() {
    this.inactivityManager.clear();
    this.props.currentRound.stopAllSounds();
    this.timers.forEach(tID => clearTimeout(tID));
    this.imgInitialize && this.imgInitialize()();
  }

  onImageClick = () => {
    this.inactivityManager.clear();
    if (this.state.revealColor) return;
    this.setState({ revealColor: true });
    this.currentAudioRecord = this.playAudio('coloringIllustration');
  };

  playAudio = sound => {
    const {
      currentRound: {
        audio,
        data: { coloringIllustration, instructions }
      }
    } = this.props;

    let audioRecord = null;
    if (sound === 'coloringIllustration') {
      audioRecord = audio[coloringIllustration.src];
    } else {
      audioRecord = audio[instructions[sound].src];
    }

    audioRecord.play();
    return audioRecord;
  };

  onNumberRevealEnd = () => {
    const tID = setTimeout(() => {
      this.setState({ revealImage: true });
      this.currentAudioRecord = this.playAudio('beforeMonochrome');
      this.currentAudioRecord.on('end', () => {
        this.finishedPlaying = true;
        this.onMonochromeRevealEnd();
      });
    }, 2000);
    this.timers.push(tID);
  };

  onMonochromeRevealEnd = () => {
    if (!this.finishedPlaying) return;
    if (this.state.revealColor) return;
    this.playAudio('beforeColor');
    this.finishedPlaying = false;
  };

  onColorRevealEnd = () => {
    this.props.onRoundEnd();
    if (this.currentAudioRecord) {
      this.currentAudioRecord.stop();
    }
    this.setState({ showAriaLabel: true });
  };

  render() {
    const { data: { text, imageColor } } = this.props.currentRound; // prettier-ignore
    const { revealColor, revealNumber, revealImage } = this.state;
    const imgClassName = revealColor ? styles.imageColor : styles.imageGrey;
    return (
      <div className={styles.container}>
        <CSSTransition
          addEndListener={node =>
            node.addEventListener('transitionend', this.onNumberRevealEnd, false)
          }
          in={revealNumber}
          mountOnEnter
          classNames="fade-800"
        >
          <p id="know-my-number-display-number" className={styles.number}>
            {text}
          </p>
        </CSSTransition>
        <CSSTransition
          addEndListener={node =>
            node.addEventListener('transitionend', this.onMonochromeRevealEnd, false)
          }
          in={revealImage}
          mountOnEnter
          onEnter={() => {
            this.imgInitialize = setInitialSize(this.imgRef, ['height']);
            this.imgInitialize();
          }}
          classNames="fade-800"
        >
          <div className={styles.image} id="know-my-number">
            <img
              tabIndex="0"
              role={this.state.showAriaLabel ? '' : 'button'}
              aria-label={this.state.showAriaLabel ? imageColor.description : t('RevealTheImage')}
              alt={imageColor.description}
              id="image-color"
              ref={node => (this.imgRef = node)}
              src={imageColor.src}
              className={imgClassName}
              onTransitionEnd={this.onColorRevealEnd}
              onClick={this.onImageClick}
              onKeyDown={ev => ev.key === 'Enter' && this.onImageClick()}
            />
          </div>
        </CSSTransition>
      </div>
    );
  }
}

KnowMyNumbers.propTypes = {
  currentRound: PropTypes.object,
  onRoundEnd: PropTypes.func
};

export default KnowMyNumbers;
